import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Alert } from '../ui/Alert';
import ResourceForm from './forms/ResourceForm';

import { Dialog, DialogHeader, DialogBody } from '../ui/Dialog';
import { settingsAPI } from '../../services/api';
import PageHeader from '../ui/PageHeader';
import { useToast } from '../ui';
import { useResourceCategories } from '../../hooks/useResourceCategories';
import MigratedResourcesView from './MigratedResourcesView';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  WrenchScrewdriverIcon,
  TruckIcon,
  CubeIcon,
  RectangleGroupIcon,
} from '@heroicons/react/24/outline';

// Iter18 §2.13 / PR-72 Commit 3 (D-07): icon presentation layer only.
// Category KEYS + LABELS come from useResourceCategories() — the shared
// derivation (dispatch 412 §1). Icons are joined on top per-page.
// Do not re-derive categories here; if you have a new caller, consume the
// hook, do not fork the derivation.
const CATEGORY_ICONS = {
  equipment: WrenchScrewdriverIcon,
  vehicles: TruckIcon,
  tools: CubeIcon,
};

const CUSTOM_CATEGORY_ICON = RectangleGroupIcon;

export default function ResourceTypesPage() {
  const toast = useToast();
  // PR-72 Commit 3 (D-07): categories from the shared hook. The page
  // keeps its own items/loading/error for the CRUD lifecycle; category
  // derivation is delegated.
  const { categories, refetch: refetchCategories } = useResourceCategories();
  const [items, setItems] = useState([]);
  // IDI-408: a migrated dealer's physical resources (legacy dbo.Resources).
  // Non-empty -> show the dealer's own types with resources under them.
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(['equipment', 'vehicles', 'tools']);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [activeCategory, setActiveCategory] = useState('equipment');

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const [res, migrated] = await Promise.all([
        settingsAPI.getList('resource_types'),
        settingsAPI.getList('resources').catch(() => []),
      ]);
      setItems(res || []);
      setResources(Array.isArray(migrated) ? migrated : []);
    } catch (err) {
      setError('Failed to load resource types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const toggleCategory = (cat) => {
    setExpandedCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const handleAdd = (category) => {
    setActiveCategory(category);
    setEditingItem(null);
    setModalOpen(true);
  };

  // REQ-WIRE-09: add a resource under a brand-new custom category.
  const handleAddCustom = () => {
    setActiveCategory('');
    setEditingItem(null);
    setModalOpen(true);
  };

  const handleEdit = (item) => {
    // IDI-408: migrated items may lack category; default to 'equipment'
    setActiveCategory(item.category || 'equipment');
    setEditingItem(item);
    setModalOpen(true);
  };

  const handleSave = async (formData) => {
    try {
      if (editingItem) {
        await settingsAPI.updateListItem('resource_types', editingItem.id, formData);
        toast.success('Resource type updated');
      } else {
        await settingsAPI.addListItem('resource_types', { ...formData, category: (formData.category || activeCategory) });
        toast.success('Resource type added');
      }
      setModalOpen(false);
      setEditingItem(null);
      await fetchItems();
      // Refresh shared category derivation so any newly-added custom
      // category is visible to other consumers (e.g. WO create sheet).
      await refetchCategories();
    } catch (err) {
      throw new Error(err.message || 'Failed to save');
    }
  };

  const handleDelete = async (item) => {
    try {
      await settingsAPI.deleteListItem('resource_types', item.id);
      toast.success('Resource type deleted');
      await fetchItems();
      // Deleting a resource may retire its custom category if it was the
      // last row carrying it.
      await refetchCategories();
    } catch (err) {
      setError('Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="space-y-3"><div className="h-8 rounded animate-pulse" style={{backgroundColor:"var(--neutral-100)"}} /><div className="h-32 rounded animate-pulse" style={{backgroundColor:"var(--neutral-100)"}} /></div>
      </div>
    );
  }

  // REQ-WIRE-09 / PR-72 Commit 3 (D-07): categories are derived by
  // useResourceCategories() — built-ins UNION distinct custom category
  // keys on existing resource rows. This page decorates the shared list
  // with icons (presentation layer); it does not re-derive.
  const allCategories = categories.map((c) => ({
    ...c,
    icon: CATEGORY_ICONS[c.key] || CUSTOM_CATEGORY_ICON,
  }));

  return (
    <div className="px-6">
      <PageHeader
        title="Resource Types"
        subtitle="Manage equipment, vehicles, and tools used in your projects. Add a custom category from the Add dialog."
        actions={
          <Button variant="primary" onClick={handleAddCustom}>
            <PlusIcon className="h-4 w-4 mr-1.5" />
            Add Custom
          </Button>
        }
      />

      {error && (
        <Alert variant="error" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {resources.length > 0 ? (
        <MigratedResourcesView types={items} resources={resources} />
      ) : (
      <div className="space-y-3">
        {allCategories.map(cat => {
          // IDI-408: migrated resource_type items may lack a category
          // field. Default uncategorized items to 'equipment' (the first
          // built-in category) so they are visible in the UI.
          const categoryItems = items.filter(i => (i.category || 'equipment') === cat.key);
          const isExpanded = expandedCategories.includes(cat.key);

          return (
            <Card key={cat.key} className="overflow-hidden">
              <button
                onClick={() => toggleCategory(cat.key)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                aria-expanded={isExpanded}
              >
                <div className="flex items-center gap-2">
                  {isExpanded ? (
                    <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                  )}
                  {cat.icon && <cat.icon className="h-4 w-4" style={{ color: 'var(--color-text-muted)' }} />}
                  <span className="text-sm font-semibold text-gray-900">{cat.label}</span>
                  <span className="text-xs text-gray-400">({categoryItems.length})</span>
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={(e) => { e.stopPropagation(); handleAdd(cat.key); }}
                >
                  <PlusIcon className="h-3 w-3 mr-1" />
                  Add
                </Button>
              </button>

              {isExpanded && (
                <div className="border-t border-gray-100">
                  {categoryItems.length === 0 ? (
                    <p className="px-4 py-6 text-center text-sm text-gray-400">
                      No {cat.label.toLowerCase()} added yet.
                    </p>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {categoryItems.map(item => (
                        <li key={item.id} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 group">
                          <div className="flex items-baseline gap-2 min-w-0 flex-1">
                            <span className="text-sm text-gray-700 truncate">{item.name}</span>
                            {item.description && (
                              <span className="text-xs text-gray-500 truncate" title={item.description}>
                                — {item.description}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => handleEdit(item)}
                              className="p-1 rounded text-gray-400 hover:text-gray-600"
                              aria-label={`Edit ${item.name}`}
                            >
                              <PencilIcon className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => handleDelete(item)}
                              className="p-1 rounded text-gray-400 hover:text-red-600"
                              aria-label={`Delete ${item.name}`}
                            >
                              <TrashIcon className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={modalOpen} onClose={() => { setModalOpen(false); setEditingItem(null); }}>
        <DialogHeader onClose={() => { setModalOpen(false); setEditingItem(null); }}>
          {editingItem ? 'Edit Resource Type' : 'Add Resource Type'}
        </DialogHeader>
        <DialogBody>
          <ResourceForm
            item={editingItem}
            category={activeCategory}
            onSave={handleSave}
            onCancel={() => { setModalOpen(false); setEditingItem(null); }}
          />
        </DialogBody>
      </Dialog>
    </div>
  );
}

// PT-22 W3-fp1 F1a: ResourceForm moved to `./forms/ResourceForm.js` so
// InlineCreate's modal can render the same Settings-shape form. Pure
// extraction — no behavior change to this page.
